// src/component/AdminGroupChat2026Migrate.js
//
// 2026 グループチャット 移行ツール（/admin/group-chat-2026-migrate）
//
// 用途：
//   グループを作り直すと groupId が変わり、チャット履歴
//   （groupChats2026/{groupId}/messages）が新グループから見えなくなる。
//   旧グループIDのメッセージを新グループIDへコピーして履歴を復旧する。
//
// 仕様：
//   - doc ID を維持してコピー → 再実行しても重複せず上書き（冪等）
//   - createdAt などの Timestamp をそのままコピー → 並び順・時刻を再現
//   - いいね(likedBy) / 返信(replyTo) / 画像(images) も丸ごと引き継ぐ
//   - 元データ（旧グループ）は削除しない
//
// 注意（Firestore ルール）：
//   messages の create ルールは通常 uid == request.auth.uid を要求するため、
//   管理者が他人のメッセージをコピーするには、messages の write ルールに
//   管理者バイパスを追加する必要があります（画面内に案内あり）。

import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Alert,
  Paper,
  CircularProgress,
  LinearProgress,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../api/firebase";

import { AppThemeContext } from "./AppShell";

const BATCH_SIZE = 400; // Firestore バッチ上限500に余裕を持たせる

export default function AdminGroupChat2026Migrate() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const appBg = theme.appBg || "#F5F5F9";
  const surface = theme.surfaceBg || "#FFFFFF";
  const accent = theme.railActive || "#5A35FF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");

  const [checking, setChecking] = useState(false);
  const [info, setInfo] = useState(null); // { fromName, fromCount, toName, toCount }
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const loadGroupName = async (gid) => {
    try {
      const snap = await getDoc(doc(db, "groupActivity2026Groups", gid));
      return snap.exists() ? snap.data()?.name || null : null;
    } catch {
      return null;
    }
  };

  const handleCheck = async () => {
    setError("");
    setSuccess("");
    setInfo(null);
    const from = fromId.trim();
    const to = toId.trim();
    if (!from || !to) {
      setError("旧グループID・新グループID の両方を入力してください");
      return;
    }
    if (from === to) {
      setError("旧IDと新IDが同じです");
      return;
    }
    setChecking(true);
    try {
      const [fromSnap, toSnap, fromName, toName] = await Promise.all([
        getDocs(collection(db, "groupChats2026", from, "messages")),
        getDocs(collection(db, "groupChats2026", to, "messages")),
        loadGroupName(from),
        loadGroupName(to),
      ]);
      setInfo({
        fromName,
        fromCount: fromSnap.size,
        toName,
        toCount: toSnap.size,
      });
    } catch (e) {
      console.error("check error:", e);
      setError(e?.message || "件数の確認に失敗しました");
    } finally {
      setChecking(false);
    }
  };

  const handleMigrate = async () => {
    const from = fromId.trim();
    const to = toId.trim();
    if (!from || !to || from === to) return;
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `旧グループ(${from}) のチャット履歴を 新グループ(${to}) へコピーします。\n` +
          "元データは削除しません。よろしいですか？"
      )
    ) {
      return;
    }
    setRunning(true);
    setError("");
    setSuccess("");
    setProgress({ done: 0, total: 0 });
    try {
      const fromCol = collection(db, "groupChats2026", from, "messages");
      const toCol = collection(db, "groupChats2026", to, "messages");
      const snap = await getDocs(fromCol);
      const docs = snap.docs;
      setProgress({ done: 0, total: docs.length });

      if (docs.length === 0) {
        setError("旧グループにコピー対象のメッセージがありません");
        setRunning(false);
        return;
      }

      let done = 0;
      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = docs.slice(i, i + BATCH_SIZE);
        chunk.forEach((d) => {
          // doc ID 維持 + フィールド丸ごと（Timestamp もそのまま）
          batch.set(doc(toCol, d.id), d.data());
        });
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
        done += chunk.length;
        setProgress({ done, total: docs.length });
      }

      // コピー後の件数を再確認
      const afterSnap = await getDocs(toCol);
      setSuccess(
        `コピー完了：${docs.length} 件をコピーしました（新グループの現在の総数：${afterSnap.size} 件）`
      );
      // 表示更新
      setInfo((prev) =>
        prev ? { ...prev, toCount: afterSnap.size, fromCount: docs.length } : prev
      );
    } catch (e) {
      console.error("migrate error:", e);
      setError(
        (e?.code === "permission-denied"
          ? "権限エラー：messages の write ルールに管理者バイパスを追加してください（下部の案内参照）。"
          : e?.message) || "コピーに失敗しました"
      );
    } finally {
      setRunning(false);
    }
  };

  const swap = () => {
    setFromId(toId);
    setToId(fromId);
    setInfo(null);
  };

  return (
    <Box sx={{ minHeight: "100%", background: appBg, pb: 6 }}>
      <Box
        sx={{
          maxWidth: 800,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: textBase, flex: 1 }}
          >
            2026 グループチャット 移行
          </Typography>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          グループを作り直して <b>groupId</b> が変わると、チャット履歴が新グループから見えなくなります。
          旧グループIDのメッセージを新グループIDへ<b>コピー</b>して復旧します（元データは残します）。
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${border}`,
            bgcolor: surface,
          }}
        >
          <Stack spacing={2}>
            <TextField
              label="旧グループID（コピー元）"
              placeholder="例：edj3KoRwKuGaL1MwJAQr"
              value={fromId}
              onChange={(e) => {
                setFromId(e.target.value);
                setInfo(null);
              }}
              fullWidth
              size="small"
            />
            <Box sx={{ textAlign: "center" }}>
              <IconButton onClick={swap} size="small" title="入れ替え">
                <SwapHorizRoundedIcon sx={{ transform: "rotate(90deg)" }} />
              </IconButton>
            </Box>
            <TextField
              label="新グループID（コピー先）"
              placeholder="例：bkMUhodYOVnU9EH8w791"
              value={toId}
              onChange={(e) => {
                setToId(e.target.value);
                setInfo(null);
              }}
              fullWidth
              size="small"
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={
                  checking ? <CircularProgress size={16} /> : <SearchRoundedIcon />
                }
                onClick={handleCheck}
                disabled={checking || running}
                sx={{
                  borderColor: accent,
                  color: accent,
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                件数を確認
              </Button>
              <Button
                variant="contained"
                startIcon={
                  running ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <PlayArrowRoundedIcon />
                  )
                }
                onClick={handleMigrate}
                disabled={running || checking || !info}
                sx={{
                  bgcolor: accent,
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: accent, opacity: 0.92 },
                }}
              >
                コピー実行
              </Button>
            </Stack>

            {info && (
              <>
                <Divider />
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      size="small"
                      label={`コピー元: ${info.fromName || "(グループdoc無し)"}`}
                      sx={{ bgcolor: `${accent}10`, color: accent, fontWeight: 600 }}
                    />
                    <Typography variant="body2" sx={{ color: textBase }}>
                      メッセージ <b>{info.fromCount}</b> 件
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      size="small"
                      label={`コピー先: ${info.toName || "(グループdoc無し)"}`}
                      variant="outlined"
                    />
                    <Typography variant="body2" sx={{ color: textBase }}>
                      現在 <b>{info.toCount}</b> 件
                    </Typography>
                  </Stack>
                  {info.fromCount === 0 && (
                    <Typography variant="caption" sx={{ color: "#c62828" }}>
                      コピー元にメッセージがありません。旧グループIDを確認してください。
                    </Typography>
                  )}
                  {info.toCount > 0 && (
                    <Typography variant="caption" sx={{ color: "#b26a00" }}>
                      コピー先に既にメッセージがあります。doc ID 一致分は上書きされます（重複はしません）。
                    </Typography>
                  )}
                </Stack>
              </>
            )}

            {running && progress.total > 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: textBase }}>
                    コピー中… {progress.done} / {progress.total}
                  </Typography>
                  <Typography variant="caption" sx={{ color: textBase, opacity: 0.7 }}>
                    {Math.round((progress.done / progress.total) * 100)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(progress.done / progress.total) * 100}
                  sx={{ height: 6, borderRadius: 999 }}
                />
              </Box>
            )}
          </Stack>
        </Paper>

        {/* ルール案内 */}
        <Paper
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: `1px dashed ${border}`,
            bgcolor: "transparent",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: textBase, mb: 1 }}>
            ⚠️ 事前に Firestore ルールの確認を
          </Typography>
          <Typography variant="caption" sx={{ color: textBase, opacity: 0.85, display: "block", mb: 1 }}>
            messages の create は通常 <code>uid == request.auth.uid</code> を要求するため、
            他人のメッセージをコピーするには、messages の write ルールに管理者バイパスが必要です。
            権限エラーが出たら、下記を <code>groupChats2026/&#123;groupId&#125;/messages</code> の
            allow に追加してください（移行後は外してOK）。
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.06)",
              fontSize: 12,
              overflowX: "auto",
              whiteSpace: "pre",
            }}
          >
{`allow create, update: if request.auth != null
  && get(/databases/$(database)/documents/users/$(request.auth.uid))
       .data.isAdmin == true;`}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
